import React, { useMemo, useState } from 'react';
import { ChevronRightIcon, TrendingUpIcon, SparklesIcon, AwardIcon, ClockIcon, UsersIcon, ZapIcon } from 'lucide-react';
import { FeaturedAppCard } from '../components/AppCard/FeaturedAppCard';
import { PopularAppCard } from '../components/AppCard/PopularAppCard';
import { PromoSection } from '../components/PromoSection';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { mockApps } from '../utils/mockData';
const categories = [{
  id: 'productivity',
  label: 'Productivity',
  icon: '⚡️',
  color: 'from-blue-500 to-cyan-500'
}, {
  id: 'creative',
  label: 'Creative',
  icon: '🎨',
  color: 'from-purple-500 to-pink-500'
}, {
  id: 'chat',
  label: 'Chat',
  icon: '💬',
  color: 'from-green-500 to-emerald-500'
}, {
  id: 'code',
  label: 'Code',
  icon: '👨‍💻',
  color: 'from-orange-500 to-red-500'
}, {
  id: 'image',
  label: 'Image',
  icon: '🖼️',
  color: 'from-indigo-500 to-purple-500'
}, {
  id: 'writing',
  label: 'Writing',
  icon: '✍️',
  color: 'from-yellow-500 to-orange-500'
}];
const collections = [{
  id: 'productivity-boost',
  title: 'Productivity Boosters',
  description: 'Apps to supercharge your workflow',
  icon: '⚡️',
  gradient: 'from-blue-500 to-cyan-500',
  appCount: 12
}, {
  id: 'creative-tools',
  title: 'Creative Tools',
  description: 'Unleash your creativity',
  icon: '🎨',
  gradient: 'from-purple-500 to-pink-500',
  appCount: 18
}, {
  id: 'business-essentials',
  title: 'Business Essentials',
  description: 'Must-have apps for professionals',
  icon: '💼',
  gradient: 'from-green-500 to-emerald-500',
  appCount: 15
}, {
  id: 'learning-growth',
  title: 'Learning & Growth',
  description: 'Expand your knowledge',
  icon: '📚',
  gradient: 'from-orange-500 to-red-500',
  appCount: 10
}];
export const Store = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const filterParam = searchParams.get('filter');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  // Filter apps based on sidebar filter
  const filteredApps = useMemo(() => {
    if (!filterParam) return mockApps;
    let filtered = [...mockApps];
    switch (filterParam) {
      case 'popular':
        filtered.sort((a, b) => {
          const aDownloads = parseInt(a.downloads.replace(/[^0-9]/g, ''));
          const bDownloads = parseInt(b.downloads.replace(/[^0-9]/g, ''));
          return bDownloads - aDownloads;
        });
        break;
      case 'featured':
        filtered = filtered.filter(app => app.rating >= 4.7);
        break;
      case 'new':
        filtered.sort((a, b) => {
          return new Date(b.updated).getTime() - new Date(a.updated).getTime();
        });
        break;
      case 'image':
        filtered = filtered.filter(app => app.category.toLowerCase().includes('image') || app.category.toLowerCase().includes('art'));
        break;
      case 'chat':
        filtered = filtered.filter(app => app.category.toLowerCase().includes('chat') || app.category.toLowerCase().includes('conversation'));
        break;
      case 'code':
        filtered = filtered.filter(app => app.category.toLowerCase().includes('code') || app.category.toLowerCase().includes('development'));
        break;
    }
    return filtered;
  }, [filterParam]);
  const getSectionTitle = () => {
    switch (filterParam) {
      case 'popular':
        return 'Popular Apps';
      case 'featured':
        return 'Featured Apps';
      case 'new':
        return 'New & Updated Apps';
      case 'image':
        return 'Image Generation Apps';
      case 'chat':
        return 'Chat & Conversation Apps';
      case 'code':
        return 'Code Generation Apps';
      default:
        return 'Featured Apps';
    }
  };
  const getSectionDescription = () => {
    switch (filterParam) {
      case 'popular':
        return 'Most downloaded apps';
      case 'featured':
        return 'Curated by our team';
      case 'new':
        return 'Recently added and updated';
      case 'image':
        return 'Create stunning visuals with AI';
      case 'chat':
        return 'Intelligent conversation partners';
      case 'code':
        return 'AI-powered development tools';
      default:
        return 'Curated by our team';
    }
  };
  // Get top apps for different sections
  const topRatedApps = useMemo(() => [...mockApps].sort((a, b) => b.rating - a.rating).slice(0, 4), []);
  const trendingApps = useMemo(() => [...mockApps].sort((a, b) => {
    const aDownloads = parseInt(a.downloads.replace(/[^0-9]/g, ''));
    const bDownloads = parseInt(b.downloads.replace(/[^0-9]/g, ''));
    return bDownloads - aDownloads;
  }).slice(0, 6), []);
  const newApps = useMemo(() => [...mockApps].sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()).slice(0, 4), []);
  return <div className="min-h-screen bg-gray-50" data-id="element-2240">
      {/* Category Pills */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-2241">
        <div className="px-4 py-3" data-id="element-2242">
          <div className="flex space-x-2 overflow-x-auto scrollbar-hide" data-id="element-2243">
            {categories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id === activeCategory ? null : category.id)} className={`flex items-center px-4 py-2 rounded-full border whitespace-nowrap text-sm transition-all ${activeCategory === category.id ? 'bg-gray-900 text-white border-transparent shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'}`} data-id="element-2244">
                <span className="mr-1.5" data-id="element-2245">{category.icon}</span>
                {category.label}
              </button>)}
          </div>
        </div>
      </div>
      <div className="px-4 py-6 space-y-8 max-w-7xl mx-auto" data-id="element-2246">
        {/* Filtered Section */}
        {filterParam ? <section data-id="element-2247">
            <div className="flex items-baseline justify-between mb-4" data-id="element-2248">
              <div data-id="element-2249">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-2250">
                  {getSectionTitle()}
                </h2>
                <p className="text-gray-500 text-sm mt-1" data-id="element-2251">
                  {getSectionDescription()}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2252">
              {filteredApps.slice(0, 6).map(app => <FeaturedAppCard key={app.id} {...app} data-id="element-2253" />)}
            </div>
            {filteredApps.length === 0 && <div className="text-center py-12" data-id="element-2254">
                <p className="text-gray-500" data-id="element-2255">No apps found for this filter.</p>
              </div>}
          </section> : <>
            {/* Hero Banner */}
            <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-8 md:p-12 text-white" data-id="element-2256">
              <div className="relative z-10 max-w-2xl" data-id="element-2257">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-sm mb-4" data-id="element-2258">
                  <SparklesIcon className="h-4 w-4 mr-2" data-id="element-2259" />
                  Featured Collection
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2260">
                  Discover AI Apps That Transform Your Work
                </h2>
                <p className="text-lg text-white/90 mb-6" data-id="element-2261">
                  Explore curated collections of powerful AI tools designed to
                  boost your productivity and creativity
                </p>
                <button onClick={() => navigate('/apps')} className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-medium hover:bg-indigo-50 transition-colors inline-flex items-center" data-id="element-2262">
                  Explore All Apps
                  <ChevronRightIcon className="ml-2 h-5 w-5" data-id="element-2263" />
                </button>
              </div>
              <div className="absolute top-0 right-0 w-1/2 h-full opacity-10" data-id="element-2264">
                <div className="absolute inset-0 bg-gradient-to-l from-white/20 to-transparent" data-id="element-2265" />
              </div>
            </section>
            {/* App of the Day */}
            <section data-id="element-2266">
              <div className="flex items-center mb-4" data-id="element-2267">
                <AwardIcon className="h-6 w-6 text-amber-500 mr-2" data-id="element-2268" />
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-2269">
                  App of the Day
                </h2>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200" data-id="element-2270">
                <div className="flex flex-col md:flex-row gap-6" data-id="element-2271">
                  <div className="flex-shrink-0" data-id="element-2272">
                    <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center" data-id="element-2273">
                      <span className="text-4xl" data-id="element-2274">🎨</span>
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-2275">
                    <div className="flex items-center gap-2 mb-2" data-id="element-2276">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-2277">
                        Featured Today
                      </span>
                      <span className="text-sm text-gray-500" data-id="element-2278">
                        Image Generation
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold mb-2" data-id="element-2279">
                      Image Wizard Pro
                    </h3>
                    <p className="text-gray-600 mb-4" data-id="element-2280">
                      Advanced image generation with custom styles and
                      modifications. Create stunning visuals in seconds with
                      AI-powered tools.
                    </p>
                    <div className="flex items-center gap-4" data-id="element-2281">
                      <button onClick={() => navigate('/app/image-wizard-pro')} className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors" data-id="element-2282">
                        Try Now
                      </button>
                      <div className="flex items-center text-sm text-gray-600" data-id="element-2283">
                        <span className="font-semibold mr-1" data-id="element-2284">4.8</span>
                        <span data-id="element-2285">⭐ (2.3k reviews)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            {/* Editor's Choice */}
            <section data-id="element-2286">
              <div className="flex items-baseline justify-between mb-4" data-id="element-2287">
                <div className="flex items-center" data-id="element-2288">
                  <SparklesIcon className="h-6 w-6 text-indigo-600 mr-2" data-id="element-2289" />
                  <div data-id="element-2290">
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-2291">
                      Editor's Choice
                    </h2>
                    <p className="text-gray-500 text-sm mt-1" data-id="element-2292">
                      Hand-picked by our team
                    </p>
                  </div>
                </div>
                <Link to="/featured" className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700" data-id="element-2293">
                  See All
                  <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2294" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2295">
                {topRatedApps.slice(0, 2).map(app => <FeaturedAppCard key={app.id} {...app} data-id="element-2296" />)}
              </div>
            </section>
            {/* Trending Now */}
            <section data-id="element-2297">
              <div className="flex items-baseline justify-between mb-4" data-id="element-2298">
                <div className="flex items-center" data-id="element-2299">
                  <TrendingUpIcon className="h-6 w-6 text-orange-500 mr-2" data-id="element-2300" />
                  <div data-id="element-2301">
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-2302">
                      Trending Now
                    </h2>
                    <p className="text-gray-500 text-sm mt-1" data-id="element-2303">
                      Most popular this week
                    </p>
                  </div>
                </div>
                <button onClick={() => navigate('/?filter=popular')} className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700" data-id="element-2304">
                  See All
                  <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2305" />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-2306">
                {trendingApps.map(app => <PopularAppCard key={app.id} id={app.id} icon={app.icon} iconBg={app.iconBg} title={app.title} category={app.category} rating={app.rating} data-id="element-2307" />)}
              </div>
            </section>
            {/* Collections */}
            <section data-id="element-2308">
              <div className="mb-4" data-id="element-2309">
                <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-2310">
                  Collections
                </h2>
                <p className="text-gray-500 text-sm" data-id="element-2311">
                  Curated app bundles for every need
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2312">
                {collections.map(collection => <button key={collection.id} onClick={() => navigate('/apps')} className="group relative overflow-hidden rounded-xl p-6 text-left transition-transform hover:scale-105" data-id="element-2313">
                    <div className={`absolute inset-0 bg-gradient-to-br ${collection.gradient} opacity-90 group-hover:opacity-100 transition-opacity`} data-id="element-2314" />
                    <div className="relative z-10" data-id="element-2315">
                      <div className="text-4xl mb-3" data-id="element-2316">{collection.icon}</div>
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2317">
                        {collection.title}
                      </h3>
                      <p className="text-white/90 text-sm mb-3" data-id="element-2318">
                        {collection.description}
                      </p>
                      <div className="flex items-center text-white/90 text-sm" data-id="element-2319">
                        <span data-id="element-2320">{collection.appCount} apps</span>
                        <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2321" />
                      </div>
                    </div>
                  </button>)}
              </div>
            </section>
            {/* Categories Showcase */}
            <section data-id="element-2322">
              <div className="mb-4" data-id="element-2323">
                <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-2324">
                  Browse by Category
                </h2>
                <p className="text-gray-500 text-sm" data-id="element-2325">
                  Find the perfect app for your needs
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-2326">
                {categories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className="group relative overflow-hidden rounded-xl p-6 text-center transition-transform hover:scale-105 bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md" data-id="element-2327">
                    <div className="text-4xl mb-2" data-id="element-2328">{category.icon}</div>
                    <h3 className="font-semibold text-gray-900" data-id="element-2329">
                      {category.label}
                    </h3>
                  </button>)}
              </div>
            </section>
            {/* New & Noteworthy */}
            <section data-id="element-2330">
              <div className="flex items-baseline justify-between mb-4" data-id="element-2331">
                <div className="flex items-center" data-id="element-2332">
                  <ClockIcon className="h-6 w-6 text-blue-500 mr-2" data-id="element-2333" />
                  <div data-id="element-2334">
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-2335">
                      New & Noteworthy
                    </h2>
                    <p className="text-gray-500 text-sm mt-1" data-id="element-2336">
                      Recently added apps
                    </p>
                  </div>
                </div>
                <button onClick={() => navigate('/?filter=new')} className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700" data-id="element-2337">
                  See All
                  <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2338" />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2339">
                {newApps.map(app => <PopularAppCard key={app.id} id={app.id} icon={app.icon} iconBg={app.iconBg} title={app.title} category={app.category} rating={app.rating} data-id="element-2340" />)}
              </div>
            </section>
            {/* Top Charts */}
            <section data-id="element-2341">
              <div className="flex items-baseline justify-between mb-4" data-id="element-2342">
                <div className="flex items-center" data-id="element-2343">
                  <ZapIcon className="h-6 w-6 text-yellow-500 mr-2" data-id="element-2344" />
                  <div data-id="element-2345">
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-2346">
                      Top Charts
                    </h2>
                    <p className="text-gray-500 text-sm mt-1" data-id="element-2347">
                      Most downloaded apps
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200" data-id="element-2348">
                {trendingApps.slice(0, 5).map((app, index) => <button key={app.id} onClick={() => navigate(`/app/${app.id}`)} className="w-full flex items-center p-4 hover:bg-gray-50 transition-colors text-left" data-id="element-2349">
                    <div className="flex-shrink-0 w-8 text-center" data-id="element-2350">
                      <span className="text-lg font-bold text-gray-400" data-id="element-2351">
                        {index + 1}
                      </span>
                    </div>
                    <div className={`w-12 h-12 ${app.iconBg} rounded-xl flex items-center justify-center mx-4`} data-id="element-2352">
                      <span className="text-white text-xl" data-id="element-2353">
                        {app.icon === 'image' ? '🖼️' : app.icon === 'message-square' ? '💬' : app.icon === 'code' ? '💻' : app.icon === 'pencil' ? '✏️' : app.icon === 'music' ? '🎵' : '🎬'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2354">
                      <h3 className="font-semibold text-gray-900 truncate" data-id="element-2355">
                        {app.title}
                      </h3>
                      <p className="text-sm text-gray-500 truncate" data-id="element-2356">
                        {app.category}
                      </p>
                    </div>
                    <div className="flex-shrink-0 ml-4 text-right" data-id="element-2357">
                      <div className="flex items-center text-sm" data-id="element-2358">
                        <span className="font-semibold mr-1" data-id="element-2359">{app.rating}</span>
                        <span className="text-gray-400" data-id="element-2360">⭐</span>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-2361">{app.downloads}</p>
                    </div>
                  </button>)}
              </div>
            </section>
            {/* Promo Section */}
            <section data-id="element-2362">
              <PromoSection data-id="element-2363" />
            </section>
            {/* Developer Spotlight */}
            <section data-id="element-2364">
              <div className="mb-4" data-id="element-2365">
                <div className="flex items-center" data-id="element-2366">
                  <UsersIcon className="h-6 w-6 text-green-500 mr-2" data-id="element-2367" />
                  <div data-id="element-2368">
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-2369">
                      Developer Spotlight
                    </h2>
                    <p className="text-gray-500 text-sm mt-1" data-id="element-2370">
                      Meet the creators behind great apps
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200" data-id="element-2371">
                <div className="flex items-start gap-4" data-id="element-2372">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0" data-id="element-2373">
                    JS
                  </div>
                  <div className="flex-1" data-id="element-2374">
                    <h3 className="text-xl font-bold mb-1" data-id="element-2375">John Smith</h3>
                    <p className="text-gray-600 mb-3" data-id="element-2376">
                      Creator of Image Wizard Pro and 5 other popular apps
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-2377">
                      <span data-id="element-2378">⭐ 4.8 average rating</span>
                      <span data-id="element-2379">•</span>
                      <span data-id="element-2380">150K+ downloads</span>
                    </div>
                  </div>
                  <button onClick={() => navigate('/apps')} className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm" data-id="element-2381">
                    View Apps
                  </button>
                </div>
              </div>
            </section>
          </>}
      </div>
    </div>;
};